import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useTranslation } from "../../../../contexts/LanguageContext";
import { useAuth } from "../../../../App";

const MobileSimilarMerchants = ({ currentMerchantId, category }) => {
  const [similarMerchants, setSimilarMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { httpClient } = useAuth(); // Get httpClient from auth context

  useEffect(() => {
    if (category) {
      fetchSimilarMerchants();
    }
  }, [category, currentMerchantId, httpClient]);

  const fetchSimilarMerchants = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use httpClient for API call
      const response = await httpClient.request(
        `/api/marketplace-category?category=${encodeURIComponent(category)}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch similar merchants');
      }

      const data = await response.json();
      
      if (data.success && data.merchants) {
        // Filter out current merchant and select random 3
        const filtered = data.merchants.filter(
          merchant => merchant.id !== currentMerchantId.toString()
        );
        
        // Shuffle and take 3
        const shuffled = [...filtered].sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, 3);
        
        setSimilarMerchants(selected);
      } else {
        setSimilarMerchants([]);
      }
    } catch (err) {
      console.error('Error fetching similar merchants:', err);
      setError(err.message || 'Failed to load similar merchants');
    } finally {
      setLoading(false);
    }
  };

  // Function to get top features from a merchant
  const getTopFeatures = (merchant) => {
    // Get features from the first plan or default to empty array
    const allFeatures = merchant.plans?.[0]?.features || [];
    
    // Return first 2 features for mobile (smaller screen), and indicate if there are more
    return {
      features: allFeatures.slice(0, 2), // Only 2 for mobile to save space
      hasMore: allFeatures.length > 2
    };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100 mx-4 my-4">
        <h3 className="text-base font-semibold text-gray-800 mb-4">
          {t('merchant.similarMerchants.title') || 'Similar Merchants'}
        </h3>
        <div className="flex justify-center py-6">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#253985]"></div>
            <span className="text-xs text-gray-500">
              {t('merchant.similarMerchants.loading') || 'Loading similar merchants...'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !similarMerchants.length) {
    // Don't show section if no similar merchants
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100 mx-4 my-4">
      {/* Header without the "View all" button */}
      <h3 className="text-base font-semibold text-gray-800 mb-4">
        {t('merchant.similarMerchants.title') || 'Similar Merchants'}
      </h3>

      {/* Horizontal scrollable container for mobile */}
      <div className="flex overflow-x-auto pb-2 -mx-1">
        <div className="flex space-x-3 px-1">
          {similarMerchants.map((merchant) => {
            const { features, hasMore } = getTopFeatures(merchant);
            const lowestPlan = merchant.plans?.length > 0 
              ? merchant.plans.reduce((lowest, plan) => 
                  (plan.price || 0) < (lowest.price || 0) ? plan : lowest, merchant.plans[0])
              : null;

            return (
              <div
                key={merchant.id}
                onClick={() => navigate(`/mobile-merchant/${merchant.id}`)}
                className="border border-gray-200 rounded-lg p-3 hover:border-[#253985] hover:shadow-sm transition-all duration-200 cursor-pointer group flex-shrink-0"
                style={{ minWidth: '260px', maxWidth: '260px' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                    {merchant.logo ? (
                      <img
                        src={merchant.logo}
                        alt={`${merchant.name} logo`}
                        className="h-8 w-8 object-contain group-hover:scale-105 transition-transform"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM3NTgxOUIiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMyA5bDktNyA5IDd2MTFhMiAyIDAgMCAxLTIgMkg1YTIgMiAwIDAgMS0yLTJ6Ij48L3BhdGg+PHBvbHlsaW5lIHBvaW50cz0iOSAyMiA5IDEyIDE1IDEyIDE1IDIyIj48L3BvbHlsaW5lPjwvc3ZnPg==';
                        }}
                      />
                    ) : (
                      <div className="text-gray-400">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-800 text-sm truncate">{merchant.name}</h4>
                    <p className="text-xs text-gray-500 truncate">{merchant.category}</p>
                  </div>
                </div>
                
                {/* Top Features Section - Compact for mobile */}
                <div className="mb-3">
                  {features.length > 0 ? (
                    <ul className="space-y-1">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-start text-xs">
                          <Check className="h-3 w-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600 line-clamp-1">{feature}</span>
                        </li>
                      ))}
                      {hasMore && (
                        <li className="text-[10px] text-gray-500 mt-0.5 ml-4.5">
                          and more...
                        </li>
                      )}
                    </ul>
                  ) : (
                    <p className="text-xs text-gray-400 italic">
                      No features listed
                    </p>
                  )}
                </div>
                
                {/* Price Section - Compact for mobile */}
                <div className="pt-2 border-t border-gray-100">
                  {lowestPlan ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs font-medium text-gray-800">
                          {t('merchant.similarMerchants.from') || 'From'} {lowestPlan.currency} {lowestPlan.price}/{lowestPlan.billing_interval}
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {merchant.plans?.length || 0} {merchant.plans?.length === 1 ? 'plan' : 'plans'}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 italic">
                      No pricing available
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MobileSimilarMerchants;